import type { CreateSiteWorkerBody, SiteWorkerView, SiteWorkersResponse, UpdateSiteWorkerBody } from '@safetyhub/shared';
import { api, type ApiResponse } from './api';

/**
 * 법정 서류 — **근로자(교육 대상자) 명부**의 API 클라이언트(M3 ⓐ). 계약 정본은 shared `features/legal-worker-roster.ts`. 모든 호출이
 * `?companyId=`를 싣는다(첫 인자 필수).
 * ⚠️ 화면 spec이 이 모듈을 `vi.mock`할 수 있으니 순수 술어·라벨을 여기 두지 않는다(형제 `legalSiteFacts.ts`와 같은 규약 — 술어·시간 파생은
 * shared `legal-worker-roster.ts`가 정본이다).
 */

export const legalWorkerRosterQueryKeys = {
  workers: (companyId: string, siteId: string) => ['legal-documents', companyId, 'sites', siteId, 'workers'] as const,
};

function unwrap<T>(body: ApiResponse<T>): T {
  if (!body.success || body.data === null) throw body;
  return body.data;
}

function workersPath(siteId: string): string {
  return `/legal-documents/sites/${encodeURIComponent(siteId)}/workers`;
}

export async function getSiteWorkers(companyId: string, siteId: string): Promise<SiteWorkersResponse> {
  const { data } = await api.get<ApiResponse<SiteWorkersResponse>>(workersPath(siteId), { params: { companyId } });
  return unwrap(data);
}

export async function createSiteWorker(companyId: string, siteId: string, body: CreateSiteWorkerBody): Promise<SiteWorkerView> {
  const { data } = await api.post<ApiResponse<SiteWorkerView>>(workersPath(siteId), body, { params: { companyId } });
  return unwrap(data);
}

export async function updateSiteWorker(
  companyId: string,
  siteId: string,
  workerId: string,
  body: UpdateSiteWorkerBody
): Promise<SiteWorkerView> {
  const { data } = await api.patch<ApiResponse<SiteWorkerView>>(`${workersPath(siteId)}/${encodeURIComponent(workerId)}`, body, {
    params: { companyId },
  });
  return unwrap(data);
}

export async function deleteSiteWorker(companyId: string, siteId: string, workerId: string): Promise<{ id: string }> {
  const { data } = await api.delete<ApiResponse<{ id: string }>>(`${workersPath(siteId)}/${encodeURIComponent(workerId)}`, {
    params: { companyId },
  });
  return unwrap(data);
}
